/**
 * Voice telemetry, one schema for both providers: each duck, barge-in decision, refused turn, muted
 * reply, tap or hold, and fallback, and a summary when the call ends. Each goes to Datadog RUM
 * (`voice.event`, `voice.call_summary`; RUM adds the user, device, OS and browser) and, through
 * `trace`, onto the turn's `kory.voice_live.turn` trace (the server's `voice_events.py`). Ids, counts,
 * durations and scores only: never the words, which stay on the trace's own `hearing` line.
 */
import { rumAction, type RumActionContext } from "@/lib/datadog-rum";

import { type BargeDecision, DETECTOR_CONFIRM_MS } from "./barge-in";
import type { NoiseReduction } from "./live-caption";

export type VoiceEventKind =
  | "duck"
  | "barge_confirmed"
  | "barge_resumed"
  | "hearing_refused"
  | "muted_reply"
  | "tap"
  | "hold"
  | "fallback";
export type VoiceEvent = {
  kind: VoiceEventKind;
  /** Since the call started. */
  at_ms: number;
  utterance_id?: string;
  /** A barge decision's deciding evidence (detector, caption, explicit); a refusal's gate (hearing, tool). */
  source?: string;
  /** A refusal's verdict (echo, unclear, ...); what fell back (provider, detector, hearing). */
  reason?: string;
  detector_speech_ms?: number;
  peak_speech_probability?: number;
};

/** The call's thresholds from the server (`voice_thresholds.py`), and the role telemetry is faceted by. */
export type VoiceTuning = { barge_speech_ms: number; speech_rms: number; role: string | null };
export const DEFAULT_TUNING: VoiceTuning = { barge_speech_ms: DETECTOR_CONFIRM_MS, speech_rms: 0.04, role: null };

const KINDS: VoiceEventKind[] = ["duck", "barge_confirmed", "barge_resumed", "hearing_refused", "muted_reply", "tap", "hold", "fallback"];
// Mic levels are sampled this often for the noise floor: ~9,000 over a 15-minute call.
const LEVEL_EVERY_MS = 100;

/** The p-th percentile (0-100) of ascending `sorted`, nearest rank; null for none. */
export function percentile(sorted: number[], p: number): number | null {
  if (!sorted.length) return null;
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1))];
}

export function createVoiceTelemetry({
  provider,
  surface,
  trace,
  now = () => performance.now(),
}: {
  provider: "openai" | "gemini";
  surface: string;
  /** Put an event on the current turn's trace. */
  trace: (event: VoiceEvent) => void;
  now?: () => number;
}) {
  const startedAt = now();
  const counts = Object.fromEntries(KINDS.map((kind) => [kind, 0])) as Record<VoiceEventKind, number>;
  const levels: number[] = [];
  let levelAt = -Infinity;
  let micClass: NoiseReduction | null = null;
  let role: string | null = null;
  let ended = false;
  const facets = () => ({ provider, surface, mic_class: micClass, role });

  const event = (fields: Omit<VoiceEvent, "at_ms">, { traced = true }: { traced?: boolean } = {}) => {
    if (ended) return;
    const full: VoiceEvent = { ...fields, at_ms: Math.round(now() - startedAt) };
    counts[full.kind] += 1;
    if (traced) trace(full);
    const context: RumActionContext = { ...facets() };
    for (const [key, value] of Object.entries(full)) if (value !== undefined) context[key] = value;
    rumAction("voice.event", context);
  };

  return {
    /** What the call learned once it started: its mic (near or far field) and the user's role. */
    describe(details: { micClass?: NoiseReduction; role?: string | null }) {
      if (details.micClass) micClass = details.micClass;
      if (details.role !== undefined) role = details.role;
    },
    /** `traced: false` when the server already put it on the trace (it decided the event). */
    event,
    /** A barge-in gate decision: real words cut Kory off, or noise and Kory went on. */
    decision(decision: BargeDecision) {
      event({
        kind: decision.outcome === "confirmed" ? "barge_confirmed" : "barge_resumed",
        utterance_id: decision.utteranceId,
        source: decision.source,
        detector_speech_ms: decision.detectorSpeechMs,
        peak_speech_probability: decision.peakSpeechProbability,
      });
    },
    /** The mic's RMS level, as the call already computes it, for the noise floor. */
    level(rms: number) {
      const at = now();
      if (ended || at - levelAt < LEVEL_EVERY_MS) return;
      levelAt = at;
      levels.push(rms);
    },
    /** The call ended: its summary, once. */
    end() {
      if (ended) return;
      ended = true;
      const sorted = [...levels].sort((a, b) => a - b);
      const summary: RumActionContext = {
        ...facets(),
        duration_ms: Math.round(now() - startedAt),
        noise_p10: percentile(sorted, 10),
        noise_p50: percentile(sorted, 50),
        noise_p90: percentile(sorted, 90),
      };
      for (const kind of KINDS) summary[`${kind}_count`] = counts[kind];
      rumAction("voice.call_summary", summary);
    },
  };
}

export type VoiceTelemetry = ReturnType<typeof createVoiceTelemetry>;
